<template>
    <ion-page>
        <ion-content>
            <div class="desktop-panel">
                <div class="aurora-container">
                    <AuroraAnimation />
                </div>
                <div class="auth-intro-header">
                    <h1>{{ $t("auth.login.title") }}</h1>
                </div>

                <div class="actions">
                    <form @submit.prevent="handleLogin">
                        <ion-input
                            id="username"
                            v-model="form.username"
                            :placeholder="$t('fields.email')"
                            name="email"
                            required
                            type="email"
                        />
                        <ion-input
                            id="password"
                            v-model="form.password"
                            :placeholder="$t('fields.password')"
                            name="password"
                            required
                            type="password"
                        />
                        <ion-button
                            class="btn"
                            expand="block"
                            type="submit"
                            @click="handleHapticFeedback"
                        >
                            {{ $t("auth.login.submit") }}
                        </ion-button>

                        <a
                            class="forgot-password"
                            @click="
                                () =>
                                    router.push({
                                        name: 'auth.forgot-password',
                                    })
                            "
                        >
                            {{ $t("auth.login.forgot") }}
                        </a>

                        <div class="bottom-action">
                            {{ $t("auth.login.no-account") }}
                            <br />
                            <span
                                class="primary"
                                @click="
                                    () => router.push({ name: 'auth.register' })
                                "
                            >
                                {{ $t("auth.login.register") }}
                            </span>
                        </div>
                    </form>
                </div>
            </div>
            <floating-back-button />
        </ion-content>
        <UpdateSplashDebug
            :visible="oauthSplashVisible"
            :progress="0"
            step=""
            :debug-lines="[]"
            logo-only
        />
    </ion-page>
</template>

<script lang="ts" setup>
    import useAuth from "@/componentables/auth";
    import { IonPage, IonContent, IonInput, IonButton } from "@ionic/vue";
    import { useRouter } from "vue-router";
    import { useRoute } from "vue-router";
    import { ref, watch } from "vue";
    import Toast from "@/componentables/toast";
    import FloatingBackButton from "@/components/FloatingBackButton.vue";
    import { useI18n } from "vue-i18n";
    import { ImpactStyle } from "@capacitor/haptics";
    import { safeHapticsImpact } from "@/utils/haptics";
    import AuroraAnimation from "@/components/animated/AuroraAnimation.vue";
    import UpdateSplashDebug from "@/components/UpdateSplashDebug.vue";

    const oauthSplashVisible = ref(false);

    const form = ref({
        username: "",
        password: "",
    });

    const router = useRouter();
    const route = useRoute();
    const auth = useAuth();
    const toast = new Toast();
    const { t } = useI18n();

    const handleHapticFeedback = () => {
        safeHapticsImpact(ImpactStyle.Medium);
    };

    watch(
        () => route.query.auth_token,
        async () => {
            if (route.query.auth_token) {
                oauthSplashVisible.value = true;
                try {
                    auth._setToken({
                        access_token: route.query.auth_token as string,
                        expires_in: "3600000",
                        refresh_token: "not_present",
                    });
                    await redirect();
                } finally {
                    oauthSplashVisible.value = false;
                }
            }
        },
        { immediate: true },
    );

    async function handleLogin() {
        try {
            await auth.login(form.value);

            await redirect();
        } catch (error: any) {
            if ([400, 401].includes(error.response?.status)) {
                await toast.open(t("auth.login.wrong-login"));
            }
        }
    }

    async function redirect() {
        const url = await auth.getRedirectAfterLogin(
            route.query.redirect
                ? { path: route.query.redirect as string }
                : null,
        );
        if (
            typeof url === "string" &&
            (url.includes("http://") || url.includes("https://"))
        ) {
            window.location.href =
                url +
                (url.includes("?") ? "&" : "?") +
                `auth_token=${await auth.getToken()}`;
        } else {
            await router.push(url);
        }
    }
</script>

<style lang="scss" scoped>
    @use "@/assets/scss/specific/auth" as *;

    .auth-intro-header {
        position: absolute;
        top: 5%;
        left: 0;
        right: 0;
        padding-bottom: 88px;

        h1 {
            font-size: vw(32px);
            text-align: center;
            font-weight: 600;
        }
    }

    .actions {
        .btn {
            margin-bottom: 16px;

            img {
                color: var(--color-white);
            }
        }

        form {
            ion-input {
                --padding-start: 16px;
                --padding-end: 16px;
                margin-bottom: 8px;
                border: 1px solid var(--color-black-alpha-10);
            }

            .forgot-password {
                cursor: pointer;
                font-size: 12px;
                font-weight: 600;
                line-height: 120%;
                text-align: center;
                color: var(--color-black-alpha-50);
                display: block;
                padding: 16px 0;
            }

            .bottom-action {
                padding-top: vw(48px);
                font-size: 12px;
                font-weight: 600;
                line-height: 120%;
                text-align: center;
                color: var(--color-black-alpha-50);
            }
        }
    }
</style>
